import { colorStrings } from "@bevco/Utilities/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export const enum fields {
    terms = "terms",
    termHeading = "heading",
    termDescription = "definition",
    displayGroupTerms = "Terms",
    displayTermHeading = "Term",
    displayTermDescription = "Definition",
}

export interface GlossaryModel {
    fields: {
        [fields.termHeading]: string;
        [fields.termDescription]: string;
    };
}

export interface OwnProps extends WidgetProps {
    fields: {
        [fields.terms]: GlossaryModel[];
    };
}

export const mapStateToProps = (_: ApplicationState, __: OwnProps) => {
    return {};
};

export type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface GlossaryWidgetStyles {
    mainWrapper: InjectableCss;
    selectWrapper: InjectableCss;
    selectStyles: SelectPresentationProps;
    termsGroupWrapper: InjectableCss;
    termsGroupKey: TypographyPresentationProps;
    term: TypographyPresentationProps;
    backButtonWrapper: InjectableCss;
    backToTopButton?: ButtonPresentationProps;
}

export const glossaryWidgetStyles: GlossaryWidgetStyles = {
    mainWrapper: {
        css: css`
            margin-top: 0;
        `,
    },
    selectWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    selectStyles: {
        backgroundColor: colorStrings.commonGlobalWhite,
        cssOverrides: {
            formInputWrapper: css`
                width: 250px;
                margin: auto;
            `,
        },
    },
    termsGroupWrapper: {
        css: css`
            margin-top: 16px;
        `,
    },
    termsGroupKey: {
        variant: "h2",
        forwardAs: "h2",
        color: colorStrings.primaryMain,
        css: css`
            position: relative;

            &::after {
                content: "";
                display: block;
                border-top: 1px solid ${getColor(colorStrings.commonBorder)};
                position: absolute;
                bottom: 10px;
                left: 30px;
                width: calc(100% - 30px);
            }
        `,
    },
    term: {
        variant: "h3",
        forwardAs: "h3",
        css: css`
            margin: 24px 0;
        `,
    },
    backButtonWrapper: {
        css: css`
            border-top: 1px solid ${getColor(colorStrings.commonBorder)};
            display: flex;
            justify-content: center;
            margin-top: 32px;
        `,
    },
    backToTopButton: {
        variant: "secondary",
        css: css`
            margin: 32px 0 16px;
            max-width: 250px;
        `,
    },
};

export const GlossaryWidget = ({ fields: { terms } }: Props) => {
    const letters = "#ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const termsGroup = [];
    const termsKeys = [];
    const [selectedLetter, setSelectedLetter] = useState("0");
    const isNumber = /\d/;

    for (let i = 0; i < letters.length; i++) {
        const key = letters.charAt(i);

        const termGroupItem = {
            key: key.toUpperCase(),

            terms: terms.filter(term => {
                if (isNumber.test(term.fields.heading.charAt(0).toLowerCase()) && key.toLocaleLowerCase() === "#") {
                    return term;
                }
                return term.fields.heading.charAt(0).toLowerCase() === key.toLocaleLowerCase();
            }),
        };

        if (termGroupItem.terms.length > 0) {
            termGroupItem.terms.sort((a, b) => (a.fields.heading > b.fields.heading ? 1 : -1));
            termsGroup.push(termGroupItem);
            termsKeys.push(key.toUpperCase());
        }
    }

    const scrollTo = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedKey = event.currentTarget.value;
        setSelectedLetter(selectedKey);
        const element = document.getElementById(`glossary_group__key-${selectedKey}`);
        if (element) {
            element.scrollIntoView();
        }
    };

    const backToTop = () => {
        setSelectedLetter("0");

        window.scrollTo(0, 0);
    };

    return (
        <StyledWrapper {...glossaryWidgetStyles.mainWrapper}>
            <StyledWrapper {...glossaryWidgetStyles.selectWrapper}>
                <Select onChange={scrollTo} {...glossaryWidgetStyles.selectStyles} value={selectedLetter}>
                    <option key="0" value="0">
                        {translate("Jump to...")}
                    </option>
                    {termsKeys.map((n: string) => (
                        <option key={n} value={n}>
                            {n}
                        </option>
                    ))}
                </Select>
            </StyledWrapper>

            {termsGroup.map(termsGroupItem => (
                <StyledWrapper key={termsGroupItem.key} {...glossaryWidgetStyles.termsGroupWrapper}>
                    <Typography
                        id={`glossary_group__key-${termsGroupItem.key}`}
                        {...glossaryWidgetStyles.termsGroupKey}
                    >
                        {parse(termsGroupItem.key, parserOptions)}
                    </Typography>
                    {termsGroupItem.terms.map(term => (
                        <StyledWrapper
                            key={`glossary_term__key-${term.fields.heading.replace(/\s/g, "-").toLowerCase()}`}
                        >
                            <Typography {...glossaryWidgetStyles.term}>
                                {parse(term.fields.heading, parserOptions)}
                            </Typography>
                            {parse(term.fields.definition, parserOptions)}
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            ))}

            <StyledWrapper {...glossaryWidgetStyles.backButtonWrapper}>
                <Button onClick={backToTop} {...glossaryWidgetStyles.backToTopButton}>
                    {translate("Back to Top")}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(GlossaryWidget) as any,
    definition: {
        displayName: "Glossary",
        group: "Verndale Box" as any,
        fieldDefinitions: [
            {
                name: fields.terms,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => item.fields[fields.termHeading],
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: fields.termHeading,
                        displayName: fields.displayTermHeading,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: fields.termDescription,
                        displayName: fields.displayTermDescription,
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
